// --------------------------------------------------------------
// Created On: 2025-04-15
// Author: Hannah Vaughan
//
// Last Modified: 2025-11-02
// Modified By: Zachary Thomas
//
// Copyright 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useMemo, useState } from "react";
import styles from "./SuperAdminDeviceSearchPage.module.scss";
import Spinner from "src/components/Spinner/Spinner";
import Card from "src/components/Card/Card";
import apiRequest from "src/utilities/api/apiRequest";
import { API } from "src/constants/miscellaneous";
import DeviceTable from "./DeviceTable/DeviceTable";
import getApiError from "src/utilities/api/getApiError";
import Toast from "src/components/Toast/Toast";
import useApi from "src/hooks/useApi";
import useReduxSelector from "src/hooks/useReduxSelector";

// Page that allows super admin users to search for devices by device type and device identifier.
export default function SuperAdminDeviceSearchPage(): Component {
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [deviceTypes, setDeviceTypes] = useState<DeviceType[]>([]);
  const [deviceTypeInput, setDeviceTypeInput] = useState<string>("");
  const [deviceIdentifierInput, setDeviceIdentifierInput] = useState<string>("");
  const [devices, setDevices] = useState<Device[]>([]);
  const [sortAscending, setSortAscending] = useState<boolean>(false);
  const [sortColumnIndex, setSortColumnIndex] = useState<number>(1);
  const sortedDevices = useMemo(() => sortDevices(devices), [devices, sortColumnIndex, sortAscending]);
  const currentUser = useReduxSelector((state) => state.currentUser);

  // Gets all device types that are accessible for the current user to select from.
  useApi(
    () => {
      setLoading(true);
      return true;
    },
    {
      method: "GET",
      url: `${API}/company/${currentUser.companyId}/asset/search/map`,
    },
    async (response: Response, responseBody: DeviceTypesResponseBody) => {
      if (response.ok && responseBody) {
        setDeviceTypes(responseBody.deviceTypes);
      }
      setLoading(false);
    },
    []
  );

  // Searches devices given the search inputs for device type and device identifier.
  async function searchDevices(): Promise<void> {
    let deviceType = "";
    let deviceIdentifier = "";
    if (deviceTypeInput.trim() !== "") {
      deviceType = deviceTypeInput;
    }
    if (deviceIdentifierInput.trim() !== "") {
      deviceIdentifier = deviceIdentifierInput;
    }

    setLoading(true);
    const requestBody = {
      deviceType: deviceType,
      deviceIdentifier: deviceIdentifier,
    };
    const [response, responseBody] = (await apiRequest(`${API}/device/search`, "POST", requestBody)) as [
      Response,
      DevicesResponseBody
    ];

    if (response.ok && responseBody) {
      setDevices(responseBody.devices);
      setErrorMessage("");
    } else {
      setDevices([]);
      setErrorMessage(await getApiError(response, "Unable to search devices."));
    }
    setLoading(false);
  }

  // Updates the sorting order.
  function updateSort(newIndex: number): void {
    if (newIndex === sortColumnIndex) {
      setSortAscending(!sortAscending);
    } else {
      setSortAscending(false);
      setSortColumnIndex(newIndex);
    }
  }

  // Sorts devices by the currently selected column index and ascension order.
  function sortDevices(devicesToSort: Device[]): Device[] {
    return devicesToSort.sort((a, b) => {
      let aValue = "";
      let bValue = "";

      switch (sortColumnIndex) {
        case 1:
          aValue = `${a.deviceIdentifier}`;
          bValue = `${b.deviceIdentifier}`;
          break;
        case 2:
          aValue = a.deviceType;
          bValue = b.deviceType;
          break;
        case 3:
          aValue = a.companyName;
          bValue = b.companyName;
          break;
        case 4:
          aValue = a.assetName || "";
          bValue = b.assetName || "";
          break;
        case 5:
          aValue = a.activationStatus || "";
          bValue = b.activationStatus || "";
          break;
        case 6:
          aValue = a.subscriptionStartDate || "";
          bValue = b.subscriptionStartDate || "";
          break;
        case 7:
          aValue = a.subscriptionExpiry || "";
          bValue = b.subscriptionExpiry || "";
          break;
        default:
          aValue = `${a.deviceIdentifier}`;
          bValue = `${b.deviceIdentifier}`;
      }

      if (aValue < bValue) {
        if (sortAscending) {
          return -1;
        } else {
          return 1;
        }
      } else if (aValue > bValue) {
        if (sortAscending) {
          return 1;
        } else {
          return -1;
        }
      } else {
        return 0;
      }
    });
  }

  // Check if the user pressed enter, if they did we search for devices.
  function checkPressEnter(e: React.KeyboardEvent): void {
    if (e.key === "Enter") {
      void searchDevices();
    }
  }

  return (
    <div className={`${styles.body} p-4`}>
      <Spinner loading={loading} />
      <Toast
        title="Error"
        message={errorMessage}
        show={errorMessage.length > 0}
        onClose={() => setErrorMessage("")}
        type="error"
      />
      <Card title="Device Search">
        <div className="m-4">
          <form className={`${styles.searchInputRow} row align-items-end`} onKeyDown={(e) => checkPressEnter(e)}>
            <div className="mb-1 col col-12 col-sm-6 col-md-5">
              <label className="mb-1">Device Type</label>
              <select
                data-test="device-search-device-type-select"
                className="form-select"
                value={deviceTypeInput}
                disabled={loading}
                onChange={(e) => setDeviceTypeInput(e.target.value)}
              >
                <option value="">All device types</option>
                {deviceTypes.map((deviceType) => (
                  <option key={deviceType.model} value={deviceType.model}>
                    {deviceType.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-1 col col-12 col-sm-6 col-md-5">
              <label className="mb-1">Device Identifier</label>
              <input
                data-test="device-search-device-identifier-input"
                className={`${styles.searchInputField} form-control mx-auto`}
                type="text"
                value={deviceIdentifierInput}
                disabled={loading}
                onChange={(e) => setDeviceIdentifierInput(e.target.value)}
              />
            </div>

            <div className="mt-2 mb-1 col align-self-end">
              <button
                data-test="device-search-button"
                type="button"
                className="btn btn-primary w-100 mx-auto"
                disabled={loading}
                onClick={() => searchDevices()}
              >
                Search
              </button>
            </div>
          </form>
        </div>
      </Card>
      <DeviceTable
        devices={sortedDevices}
        deviceTypes={deviceTypes}
        sortColumnIndex={sortColumnIndex}
        sortAscending={sortAscending}
        onUpdateSort={(newIndex) => updateSort(newIndex)}
      />
    </div>
  );
}

interface DeviceTypesResponseBody {
  deviceTypes: DeviceType[];
}

interface DeviceType {
  model: string;
  name: string;
}

interface DevicesResponseBody {
  devices: Device[];
}

interface Device {
  deviceType: string;
  deviceIdentifier: number;
  assetId: number;
  assetName: string;
  companyId: number;
  companyName: string;
  isPackager: boolean;
  subscriptionExpiry: string;
  subscriptionStartDate: string;
  activationStatus: string;
}
